import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { IonicModule, ModalController, NavController, Platform } from '@ionic/angular';
import { AuthService } from '../auth.service';
import { ToastService } from '../../_core/services/toast.service';
import { finalize, take } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { images } from '../../_shared/data/image';
import { CommonModule } from '@angular/common';
import { TermsAndConditionsComponent } from '../../_shared/components/terms-and-conditions/terms-and-conditions.component';
import { PrivacyPolicyComponent } from '../../_shared/components/privacy-policy/privacy-policy.component';
import { SsoButtonsComponent } from '../../_shared/components/sso-buttons/sso-buttons.component';
import { VerificationOtpComponent } from '../verification-otp/verification-otp.component';
import { IS_NEW_UI_FLOW } from '../../../environments/environment';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { trimEdgeSpaces, noLeadingSpaceValidator, trimEdgeSpacesOnBlurOrSubmit } from '../../_shared/utils/utils';

@Component({
  selector: 'app-create-user',
  templateUrl: './create-user.component.html',
  styleUrls: ['./create-user.component.scss'],
  standalone: true,
  imports: [IonicModule, ReactiveFormsModule, CommonModule, SsoButtonsComponent, TranslateModule]
})
export class CreateUserComponent implements OnInit {
  private platform = inject(Platform);
  form: FormGroup;
  isLoading: boolean = false;
  attemptedSubmit: boolean = false;
  public logoSquare = images.logoSquare;
  isEmailLogin: boolean = false;
  showPassword: boolean = false;
  @Output() goBackPage = new EventEmitter<string>();
  @Input({ required: false }) isLoginModal = false;
  public screenHeight: number = this.platform.height();
  public isVerificationOtpModal: boolean = false;
  private trimSubscriptions: Subscription[] = [];

  private normalizeEmail(value: string | null | undefined): string {
    return value?.trim().toLowerCase() ?? '';
  }

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private navCtrl: NavController,
    private toastService: ToastService,
    private router: Router,
    private modalController: ModalController,
    private route: ActivatedRoute
  ) { }

  ngOnInit() {
    this.buildForm();
  }

  ngOnDestroy() {
    this.trimSubscriptions.forEach(sub => sub.unsubscribe());
  }

  buildForm() {
    this.form = this.fb.group({
      first_name: ['', [Validators.required, Validators.minLength(2), noLeadingSpaceValidator()]],
      last_name: ['', [Validators.required, Validators.minLength(2), noLeadingSpaceValidator()]],
      email: ['', [Validators.required, Validators.email, noLeadingSpaceValidator()]],
      password: ['', [
        Validators.required,
        Validators.minLength(8),
        Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/)
      ]]
    });
    trimEdgeSpaces(this.trimSubscriptions, this.form, ['first_name', 'last_name', 'email']);
  }

  async createAccount() {
    this.trimSpaces();
    this.attemptedSubmit = true;

    if (this.form.valid) {
      this.isLoading = true;

      const formData = this.form.value;
      const { ...userData } = formData;
      userData.email = this.normalizeEmail(userData.email);
      this.form.patchValue({ email: userData.email }, { emitEvent: false });

      const payload = {
        user: userData,
      }
      this.authService.createAccount(payload)
        .pipe(
          take(1),
          finalize(() => this.isLoading = false)
        )
        .subscribe({
          next: async res => {
            this.toastService.presentToast(res.message);
            if (this.isLoginModal) {
              this.modalController.dismiss();
              this.isVerificationOtpModal = true;
              const modal = await this.modalController.create({
                component: VerificationOtpComponent,
                cssClass: 'fullscreen-modal',
                componentProps: {
                  state: {
                    phone: userData.phone,
                    country_code: userData.country_code,
                    email: userData.email,
                    password: userData.password,
                    isLoginModal: this.isLoginModal
                  }
                }
              });

              await modal.present();
            } else {


              this.navCtrl.navigateForward(`/${IS_NEW_UI_FLOW ? 'auth' : 'login'}/verification-otp`, {
                state: {
                  phone: userData.phone,
                  country_code: userData.country_code,
                  email: userData.email,
                  password: userData.password
                }
              });
            }
          },
          error: (error) => {
            const errorMessage = error?.error?.message || error?.message || 'Failed to create account. Please try again.';
            this.toastService.presentDangerToast(errorMessage);
          }
        });
    } else {
      this.handleErrors();
    }
  }

  async changeForm() {
    if (this.isLoginModal) {
      this.modalController.dismiss();
    
      const modal = await this.modalController.create({
        component: VerificationOtpComponent,
        cssClass: 'fullscreen-modal',
        componentProps: {
          isManualVerification: true,
          state: {
            isLoginModal: this.isLoginModal
          }
        }
      });

      await modal.present();
    } else {
      this.navCtrl.navigateForward(`/${IS_NEW_UI_FLOW ? 'auth' : 'login'}/verification-otp`, {
        state: {
          isManualVerification: true
        }
      });
    }
  }

  trimSpaces() {
    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.get(key);
      if (control?.value != 'password' && control) { // Avoid trimming password field here
        trimEdgeSpacesOnBlurOrSubmit(control);
      }
    });
  }

  trimEdgeSpacesIonInput(form: FormGroup, controlName: string, event: any) {
    const value = event.target.value;
    if (value) {
      const trimmedValue = value.trim();
      form.get(controlName)?.setValue(trimmedValue, { emitEvent: false });
    }
  }

  private handleErrors() {
    let errorMessage = '';

    if (this.form.get('first_name')?.invalid) {
      errorMessage = 'First name must have minimum 2 characters.';
    } else if (this.form.get('last_name')?.invalid) {
      errorMessage = 'Last name must have minimum 2 characters.';
    } else if (this.form.get('email')?.invalid) {
      errorMessage = 'Please enter a valid email address.';
    } else if (this.form.get('password')?.invalid) {
      errorMessage = 'Password must have at least 8 characters with uppercase, lowercase, and a number.';
    }

    this.toastService.presentDangerToast(errorMessage);
  }

  getPasswordStrength(): string {
    const password = this.form.get('password')?.value;
    if (!password) return 'weak';

    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;

    if (strength >= 4) return 'strong';
    if (strength >= 3) return 'medium';
    return 'weak';
  }

  isPasswordLengthValid(): boolean {
    return (this.form.get('password')?.value || '').length >= 8;
  }

  isPasswordCaseValid(): boolean {
    const value = this.form.get('password')?.value || '';
    return /[a-z]/.test(value) && /[A-Z]/.test(value);
  }

  isPasswordNumberValid(): boolean {
    return /\d/.test(this.form.get('password')?.value || '');
  }

  async openTermsAndConditions() {
    const modal = await this.modalController.create({
      component: TermsAndConditionsComponent,
    });

    await modal.present();
  }

  async openPrivacyPolicy() {
    const modal = await this.modalController.create({
      component: PrivacyPolicyComponent,
    });

    await modal.present();
  }

  continueWithEmailAndPhone() {
    this.isEmailLogin = true;
  }

  async loginWithSSO(provider: 'Google' | 'Apple') {
    try {
      // Show loading indicator
      // await this.loaderService.present();

      // Get SSO URL with app detection
      const ssoUrl = await this.authService.getSSOLoginUrlWithAppDetection(provider);
      // Store current URL for return after SSO
      this.authService.setSsoReturnUrl('/main-menu');

      // Use window.location.href to force external browser
      // This works for both iOS and Android
      window.location.href = ssoUrl;

    } catch (error) {
      await this.toastService.presentDangerToast('Failed to open SSO login. Please try again.');
    } finally {
      // Dismiss loading indicator
      // await this.loaderService.dismiss();
    }
  }


  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  goBack() {
    this.goBackPage.emit('auth');
  }
}